import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { NotificationRegion, type NotificationItem, type NotificationTone } from "@nbs/ui";

export type NotificationContextValue = {
  notify: (message: string, tone?: NotificationTone) => string;
  dismiss: (id: string) => void;
  clear: () => void;
};

const NotificationContext = createContext<NotificationContextValue | null>(null);

let counter = 0;

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<NotificationItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback(
    (message: string, tone: NotificationTone = "info") => {
      const id = `toast-${++counter}`;
      setItems((prev) => [...prev, { id, message, tone }]);
      window.setTimeout(() => dismiss(id), 5000);
      return id;
    },
    [dismiss],
  );

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => ({ notify, dismiss, clear }), [notify, dismiss, clear]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationRegion items={items} onDismiss={dismiss} />
    </NotificationContext.Provider>
  );
}

export function useNotificationContext(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotificationContext must be used within NotificationProvider");
  }
  return ctx;
}
